// Unified CEU Service - switches between API and DynamoDB based on configuration
import dynamoCEUService from './dynamoCEUService';
import apiCEUService from './apiCEUService';
import mockDataService from './mockDataService';
import { trackFeatureUsage } from '../lib/analytics/usageTracking';

// Simple in-memory cache for CEU data (5 minute TTL)
const ceuCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getCacheKey = (userId, operation) => `${userId}_${operation}`;

const getCachedData = (cacheKey) => {
  const cached = ceuCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  return null;
};

const setCachedData = (cacheKey, data) => {
  ceuCache.set(cacheKey, {
    data,
    timestamp: Date.now()
  });
  
  // Clean up old cache entries
  if (ceuCache.size > 100) {
    const oldestKey = ceuCache.keys().next().value;
    ceuCache.delete(oldestKey);
  }
};

// Configuration to determine which service to use
const isLocalDevelopment = window.location.hostname === 'localhost';
const isHostedDev = window.location.hostname === 'dev.d1d7vib6srssm3.amplifyapp.com';
const isProduction = window.location.hostname === 'getcertifiable.com' || 
                    window.location.hostname === 'www.certifiable.me';

// Check for query parameter overrides (for testing)
const urlParams = new URLSearchParams(window.location.search);
const forceAPI = urlParams.get('useAPI') === 'true';
const forceDynamoDB = urlParams.get('useDynamoDB') === 'true';

// Service selection logic:
// All environments now use API Gateway
let USE_API = true;

if (forceAPI) {
  USE_API = true;
  if (process.env.NODE_ENV === 'development') console.log('🔧 Forced API usage via query parameter');
} else if (forceDynamoDB) {
  USE_API = false;
  if (process.env.NODE_ENV === 'development') console.log('🔧 Forced DynamoDB usage via query parameter');
}

// Environment detection (logging only in development)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  console.log('🌍 Environment Detection:', {
    hostname: window.location.hostname,
    serviceUsed: USE_API ? 'API Gateway' : 'DynamoDB Direct'
  });
}

// Service selector
const getService = () => {
  return USE_API ? apiCEUService : dynamoCEUService;
};

// Unified interface
export const ceuService = {
  async getCEUs(userIdentifier) {
    if (!userIdentifier) {
      return { success: false, error: 'No user identifier provided', data: [] };
    }
    
    // Check cache first for API calls
    const cacheKey = getCacheKey(userIdentifier, 'getCEUs');
    if (USE_API) {
      const cachedResult = getCachedData(cacheKey);
      if (cachedResult) {
        trackFeatureUsage('ceu_list_view', { 
          count: cachedResult.data?.length || 0,
          responseTime: 0,
          serviceType: 'cache'
        });
        return cachedResult;
      }
    }
    
    const startTime = performance.now();
    
    try {
      const service = getService();
      let result;
      
      if (USE_API) {
        result = await service.getCEUs(userIdentifier);
        // API service now returns { success: boolean, data: array, error?: string }
        if (result.success) {
          const responseTime = performance.now() - startTime;
          
          // Cache successful API results
          if (USE_API) {
            setCachedData(cacheKey, result);
          }
          
          trackFeatureUsage('ceu_list_view', { 
            count: result.data?.length || 0,
            responseTime: Math.round(responseTime),
            serviceType: 'api'
          });
          return result;
        } else {
          throw new Error(result.error || 'API request failed');
        }
      } else {
        // Map to DynamoDB service method
        result = await service.getUserCEUs(userIdentifier);
        if (result.success) {
          trackFeatureUsage('ceu_list_view', { count: result.data?.length || 0 });
        }
        return result.success ? result : { success: false, error: result.error, data: [] };
      }
    } catch (error) {
      // Enhanced fallback logic
      if (USE_API && (isHostedDev || isLocalDevelopment)) {
        try {
          const dynamoResult = await dynamoCEUService.getUserCEUs(userIdentifier);
          if (dynamoResult.success) {
            return dynamoResult;
          }
        } catch (dynamoError) {
          // Continue to mock fallback
        }
      }
      
      // Final fallback to mock data
      try {
        const mockResult = await mockDataService.getCEUs(userIdentifier);
        return mockResult;
      } catch (mockError) {
        return { success: false, error: error.message, data: [] };
      }
    }
  },

  async getCEU(userId, ceuId) {
    const service = getService();
    if (USE_API) {
      try {
        const result = await service.getCEU(userId, ceuId);
        if (result) {
          return { success: true, data: result };
        } else {
          return { success: false, error: 'CEU not found' };
        }
      } catch (error) {
        return { success: false, error: error.message };
      }
    } else {
      // Map to DynamoDB service method - return full result object
      return await service.getCEU(userId, ceuId);
    }
  },

  async createCEU(userId, ceuData) {
    const service = getService();
    
    if (USE_API) {
      try {
        const result = await service.createCEU(userId, ceuData);
        
        // Invalidate cache after successful creation
        const cacheKey = getCacheKey(userId, 'getCEUs');
        ceuCache.delete(cacheKey);
        
        trackFeatureUsage('ceu_create', { category: ceuData.category, ceuHours: ceuData.ceuHours });
        return { success: true, data: result };
      } catch (error) {
        return { success: false, error: error.message };
      }
    } else {
      // Map to DynamoDB service method
      const result = await service.saveCEU({ ...ceuData, userId });
      trackFeatureUsage('ceu_create', { category: ceuData.category, ceuHours: ceuData.ceuHours });
      // DynamoDB service returns the CEU object directly, wrap it in success format
      return { success: true, data: result };
    }
  },

  // Alias for backward compatibility
  saveCEU: async function(ceuData) {
    return ceuService.createCEU(ceuData.userId, ceuData);
  },

  async updateCEU(userId, ceuId, updates) {
    const service = getService();
    if (USE_API) {
      return service.updateCEU(userId, ceuId, updates);
    } else {
      // Map to DynamoDB service method
      const result = await service.updateCEU(userId, ceuId, updates);
      return result.success ? result.data : null;
    }
  },

  async deleteCEU(userId, ceuId) {
    const service = getService();
    try {
      if (USE_API) {
        const result = await service.deleteCEU(userId, ceuId);
        return { success: true, data: result };
      } else {
        // Map to DynamoDB service method
        const result = await service.deleteCEU(userId, ceuId);
        
        // DynamoDB service returns { success: true } or { success: false, error: string }
        if (result && result.success) {
          return { success: true, data: { deleted: true, ceuId } };
        } else {
          return { success: false, error: result?.error || 'Delete operation failed' };
        }
      }
    } catch (error) {
      console.error('❌ Delete CEU error:', error);
      return { success: false, error: error.message || 'Unknown delete error' };
    }
  },

  async getAllCEUs() {
    const service = getService();
    if (USE_API) {
      try {
        const result = await service.getAllCEUs();
        return result;
      } catch (error) {
        console.error('Error getting all CEUs:', error);
        return { success: false, error: error.message, data: [] };
      }
    } else {
      // Map to DynamoDB service method
      const result = await service.getAllCEUsForAnalytics();
      return result.success ? result.ceus : [];
    }
  },

  async getCEUsByDateRange(userId, startDate, endDate) {
    const service = getService();
    if (USE_API) {
      return service.getCEUsByDateRange(userId, startDate, endDate);
    } else {
      // Get all CEUs and filter by date range
      const result = await service.getUserCEUs(userId);
      if (!result.success) return [];
      
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      return result.data.filter(ceu => {
        const ceuDate = new Date(ceu.date);
        return ceuDate >= start && ceuDate <= end;
      });
    }
  },

  async searchCEUs(userId, searchTerm) {
    const service = getService();
    if (USE_API) {
      return service.searchCEUs(userId, searchTerm);
    } else {
      // Get all CEUs and search
      const result = await service.getUserCEUs(userId);
      if (!result.success) return [];
      
      const searchLower = searchTerm.toLowerCase();
      return result.data.filter(ceu => 
        ceu.title?.toLowerCase().includes(searchLower) ||
        ceu.description?.toLowerCase().includes(searchLower) ||
        ceu.category?.toLowerCase().includes(searchLower) ||
        ceu.provider?.toLowerCase().includes(searchLower)
      );
    }
  },

  async getCEUStats(userId) {
    const service = getService();
    if (USE_API) {
      return service.getCEUStats(userId);
    } else {
      // Get all CEUs and calculate stats
      const result = await service.getUserCEUs(userId);
      if (!result.success) return { totalHours: 0, totalCEUs: 0, categoryStats: {}, recentCEUs: [] };
      
      const allCEUs = result.data;
      const totalHours = allCEUs.reduce((sum, ceu) => sum + (parseFloat(ceu.ceuHours) || 0), 0);
      const totalCEUs = allCEUs.length;
      
      const categoryStats = allCEUs.reduce((stats, ceu) => {
        const category = ceu.category || 'Uncategorized';
        stats[category] = (stats[category] || 0) + (parseFloat(ceu.ceuHours) || 0);
        return stats;
      }, {});

      return {
        totalHours,
        totalCEUs,
        categoryStats,
        recentCEUs: allCEUs
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 5)
      };
    }
  }
};

export default ceuService;
